import React, { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, Trash, Scales, TrendUp, ChartLine } from "@phosphor-icons/react";
import { toast } from "sonner";

const monthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10);
  const to = now.toISOString().slice(0, 10);
  return { from, to };
};

const EXPENSE_CATEGORIES = [
  "rent",
  "salary",
  "utilities",
  "marketing",
  "supplies",
  "travel",
  "other",
];

export default function Accounting() {
  const init = monthRange();
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="accounting-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Finance
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Accounting
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Trial Balance, P&L, Balance Sheet and operating expenses.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              From
            </Label>
            <Input
              data-testid="acc-from"
              type="date"
              className="mt-1.5 bg-card border-border"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              To
            </Label>
            <Input
              data-testid="acc-to"
              type="date"
              className="mt-1.5 bg-card border-border"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
      </header>

      <Tabs defaultValue="pl" className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="pl" data-testid="acc-tab-pl">
            Profit & Loss
          </TabsTrigger>
          <TabsTrigger value="tb" data-testid="acc-tab-tb">
            Trial Balance
          </TabsTrigger>
          <TabsTrigger value="bs" data-testid="acc-tab-bs">
            Balance Sheet
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="acc-tab-expenses">
            Expenses
          </TabsTrigger>
        </TabsList>
        <TabsContent value="pl">
          <PL from={from} to={to} />
        </TabsContent>
        <TabsContent value="tb">
          <TB from={from} to={to} />
        </TabsContent>
        <TabsContent value="bs">
          <BS asOf={to} />
        </TabsContent>
        <TabsContent value="expenses">
          <Expenses from={from} to={to} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function PL({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get(`/accounting/profit-loss?from_date=${from}&to_date=${to}`)
      .then((r) => setData(r.data));
  }, [from, to]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Big label="Revenue" value={inr(data.revenue)} />
        <Big label="COGS" value={inr(data.cost_of_goods_sold)} />
        <Big label="Gross Profit" value={inr(data.gross_profit)} accent />
        <Big
          label={`Net Profit (${data.net_margin_percent}%)`}
          value={inr(data.net_profit)}
          accent={data.net_profit >= 0}
          bad={data.net_profit < 0}
        />
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Account</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TR l="Revenue (Net of GST)" v={data.revenue} bold />
            <TR
              l="Less: Cost of Goods Sold (Purchases)"
              v={-data.cost_of_goods_sold}
            />
            <TR l="Gross Profit" v={data.gross_profit} bold accent />
            {Object.entries(data.expenses).map(([k, v]) => (
              <TR key={k} l={`Less: ${k}`} v={-v} />
            ))}
            <TR l="Total Operating Expenses" v={-data.total_expenses} />
            <TR
              l={`Net Profit (Margin ${data.net_margin_percent}%)`}
              v={data.net_profit}
              bold
              accent
            />
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function TB({ from, to }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get(`/accounting/trial-balance?from_date=${from}&to_date=${to}`)
      .then((r) => setData(r.data));
  }, [from, to]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Big label="Total Debit" value={inr(data.total_debit)} />
        <Big label="Total Credit" value={inr(data.total_credit)} />
        <Big
          label="Difference"
          value={inr(data.difference)}
          accent={Math.abs(data.difference) < 1}
          bad={Math.abs(data.difference) >= 1}
        />
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Account</TableHead>
              <TableHead className="text-right">Debit</TableHead>
              <TableHead className="text-right">Credit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell>{r.account}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.debit ? inr(r.debit) : ""}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.credit ? inr(r.credit) : ""}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-secondary/40">
              <TableCell className="font-display">Totals</TableCell>
              <TableCell className="text-right tabular-nums font-display">
                {inr(data.total_debit)}
              </TableCell>
              <TableCell className="text-right tabular-nums font-display">
                {inr(data.total_credit)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function BS({ asOf }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get(`/accounting/balance-sheet?as_of=${asOf}`)
      .then((r) => setData(r.data));
  }, [asOf]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Big label="Total Assets" value={inr(data.total_assets)} accent />
        <Big
          label="Liabilities + Equity"
          value={inr(data.total_liabilities_equity)}
          accent
        />
        <Big
          label="Balanced"
          value={data.balanced ? "✓" : "✗"}
          accent={data.balanced}
          bad={!data.balanced}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Section title="Assets" data={data.assets} total={data.total_assets} />
        <Section
          title="Liabilities & Equity"
          data={data.liabilities}
          total={data.total_liabilities_equity}
        />
      </div>
    </div>
  );
}

function Section({ title, data, total }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-4 py-2 bg-secondary/40 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        {title}
      </div>
      <Table>
        <TableBody>
          {Object.entries(data).map(([k, v]) => (
            <TableRow key={k}>
              <TableCell className="capitalize">
                {k.replace(/_/g, " ")}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {inr(v)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow className="bg-secondary/20">
            <TableCell className="font-display">Total</TableCell>
            <TableCell className="text-right tabular-nums font-display text-gold">
              {inr(total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function Expenses({ from, to }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category: "rent",
    amount: 0,
    description: "",
    date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    const { data } = await api.get(`/expenses?from_date=${from}&to_date=${to}`);
    setList(data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [from, to]);

  const save = async () => {
    if (!Number(form.amount)) {
      toast.error("Amount required");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await api.post("/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense logged");
      setOpen(false);
      setForm({
        category: "rent",
        amount: 0,
        description: "",
        date: new Date().toISOString().slice(0, 10),
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm("Delete expense?")) return;
    await api.delete(`/expenses/${e.id}`);
    load();
  };

  const total = list.reduce((a, e) => a + (e.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-muted-foreground text-sm">
          Total <span className="text-gold tabular-nums">{inr(total)}</span>{" "}
          across {list.length} entries.
        </div>
        <Button
          data-testid="add-expense-btn"
          onClick={() => setOpen(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={14} className="mr-1" /> Add expense
        </Button>
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="text-center py-10 text-muted-foreground"
                >
                  No expenses logged.
                </TableCell>
              </TableRow>
            ) : (
              list.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="text-sm text-muted-foreground">
                    {e.date}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs uppercase tracking-wider px-2 py-1 rounded bg-secondary border border-border">
                      {e.category}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {e.description || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(e.amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={() => remove(e)}
                      className="p-1.5 rounded-md hover:bg-destructive/15 text-muted-foreground hover:text-destructive"
                    >
                      <Trash size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              New Expense
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Category
              </Label>
              <select
                data-testid="expense-category"
                className="mt-1.5 w-full h-10 rounded-md bg-background border border-border px-3"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {EXPENSE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Amount *
              </Label>
              <Input
                data-testid="expense-amount"
                type="number"
                step="0.01"
                className="mt-1.5 bg-background border-border tabular-nums"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Description
              </Label>
              <Input
                className="mt-1.5 bg-background border-border"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Date
              </Label>
              <Input
                type="date"
                className="mt-1.5 bg-background border-border"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-expense-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Big = ({ label, value, accent, bad }) => (
  <div
    className={`rounded-lg border p-4 ${accent ? "border-gold/40 bg-gold/5" : bad ? "border-rose-500/30 bg-rose-500/5" : "border-border bg-card"}`}
  >
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    <div
      className={`font-display text-2xl mt-1 tabular-nums ${accent ? "text-gold" : bad ? "text-rose-400" : ""}`}
    >
      {value}
    </div>
  </div>
);

const TR = ({ l, v, bold, accent }) => (
  <TableRow className={accent ? "bg-gold/5" : ""}>
    <TableCell className={bold ? "font-display" : ""}>{l}</TableCell>
    <TableCell
      className={`text-right tabular-nums ${bold ? "font-display" : ""} ${accent ? "text-gold" : v < 0 ? "text-muted-foreground" : ""}`}
    >
      {inr(v)}
    </TableCell>
  </TableRow>
);
